/**
 * Reading the room's answer: how strongly it is held, what moved, and what it
 * rubbed out.
 *
 * The line at the front of the room is the one thing a person watches while a
 * dig runs, so everything here is about making a WEAK answer readable rather
 * than making any answer look good. A stance held on one source has to say so in
 * the same breath it takes the stance, or the reader learns to distrust the line
 * and the whole artifact is worth nothing.
 *
 * Pure, in its own module, because the component it serves pulls in the room —
 * CRA's jest cannot resolve react-router-dom, and logic behind that import
 * cannot be tested at all.
 */
import type { BoardroomAnswer, BoardroomAnswering } from '../../../types';

/** Weakest first, so a comparison is an index comparison. */
export const STRENGTHS = ['hunch', 'leaning', 'likely', 'confident'] as const;

export const strengthRank = (s: string | null | undefined): number => {
  const i = STRENGTHS.indexOf((s ?? '').toLowerCase() as (typeof STRENGTHS)[number]);
  return i < 0 ? 0 : i;
};

/** A strength in words a reader uses. "hunch" is the room's word. */
export function strengthWords(s: string | null | undefined): string {
  return (s ?? '').toLowerCase() === 'hunch' ? 'early guess' : (s ?? '');
}

/** How a strength reads on the line. Not a colour scale from bad to good: a
 *  hunch honestly labelled is not a failure, it is the room being early. */
export function strengthTone(s: string | null | undefined): string {
  switch ((s ?? '').toLowerCase()) {
    case 'confident':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'likely':
      return 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300';
    case 'leaning':
      return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400';
  }
}

/** What the room DID, in words a person reads rather than a state name. */
export function movementWords(a: Pick<BoardroomAnswer, 'movement'> | null | undefined): string {
  switch (a?.movement) {
    case 'first':
      return 'first answer';
    case 'sharpened':
      return 'sharpened';
    case 'reversed':
      return 'changed its mind';
    case 'widened':
      return 'widened';
    case 'narrowed':
      return 'narrowed';
    case 'unchanged':
      return 'unchanged';
    default:
      return '';
  }
}

/** A reversal is the one thing on this line that must not be missed — a reader
 *  who skims past it is reading an answer whose predecessor they still believe. */
export const isReversal = (a: Pick<BoardroomAnswer, 'movement'> | null | undefined): boolean =>
  a?.movement === 'reversed';

/**
 * How long the answer has stood still, counted on the CLIENT from the run's
 * live turn count — so it keeps climbing between passes instead of freezing
 * until the next one lands.
 *
 * NOTHING HERE STOPS ANYTHING. It is shown beside Stop so a person can decide
 * they have seen enough; a room that stopped itself because a line settled would
 * be a room that gave up early with nobody to ask.
 */
export function quietTurns(
  answer: Pick<BoardroomAnswer, 'turns_at' | 'movement'> | null | undefined,
  turnsDone: number | null | undefined,
): number {
  if (!answer) return 0;
  return Math.max(0, (turnsDone ?? 0) - (answer.turns_at ?? 0));
}

/** The quiet line, or null while the room is still plainly working.
 *
 *  Under three turns there is nothing to say: every answer is briefly still, and
 *  a line that nags after one turn would train the reader to ignore it. */
export function quietLine(
  answer: Pick<BoardroomAnswer, 'turns_at' | 'movement'> | null | undefined,
  turnsDone: number | null | undefined,
  { min = 3 }: { min?: number } = {},
): string | null {
  const n = quietTurns(answer, turnsDone);
  if (!answer || n < min) return null;
  return `the answer has not moved in ${n} turns`;
}

/**
 * What the room did and how long ago, as ONE phrase.
 *
 * These were two facts side by side — "changed its mind" and "the answer has not
 * moved in 3 turns" — which read as a contradiction even though both were true:
 * it reversed, three turns ago, and nothing has moved it since. A movement is a
 * thing that HAPPENED, so it only reads properly with its distance attached.
 *
 * Still nothing to act on: this is the same count, said once instead of twice.
 */
export function movementLine(
  answer: Pick<BoardroomAnswer, 'movement' | 'turns_at'> | null | undefined,
  turnsDone: number | null | undefined,
  { working = false }: { working?: boolean } = {},
): string {
  if (!answer) return '';
  const what = movementWords(answer);
  // While a pass is in flight, how long it has been still is about to change.
  if (working) return what ? `${what} · rethinking…` : 'rethinking…';
  const n = quietTurns(answer, turnsDone);
  if (n < 1) return what;
  return `${what} · ${n} turn${n === 1 ? '' : 's'} ago, nothing since`;
}

/** Why it is not stronger, as one readable clause. */
export function cappedNote(a: Pick<BoardroomAnswer, 'strength_capped'> | null | undefined): string {
  const note = (a?.strength_capped ?? '').trim();
  return note ? `on ${note}` : '';
}

/** The counted board behind it — "3 sources, 2 voices, 1 checked" — so the rung
 *  can be argued with rather than believed. */
export function supportWords(a: Pick<BoardroomAnswer, 'support'> | null | undefined): string {
  const s = a?.support ?? {};
  const bits: string[] = [];
  const n = s.receipts ?? 0;
  if (n) bits.push(`${n} source${n === 1 ? '' : 's'}`);
  const p = s.publishers ?? 0;
  if (p) bits.push(`${p} voice${p === 1 ? '' : 's'}`);
  if (s.holds) bits.push(`${s.holds} checked`);
  if (s.does_not_hold) bits.push(`${s.does_not_hold} did not hold`);
  return bits.join(' · ');
}

export interface ClaimDiff {
  added: string[];
  gone: string[];
}

/** What changed between two versions, by claim key — so a reworded claim is the
 *  same claim and only a real change shows as one. */
export function claimDiff(
  before: Pick<BoardroomAnswer, 'claims'> | null | undefined,
  after: Pick<BoardroomAnswer, 'claims'> | null | undefined,
): ClaimDiff {
  const was = new Map((before?.claims ?? []).map((c) => [c.key, c.text]));
  const now = new Map((after?.claims ?? []).map((c) => [c.key, c.text]));
  const added: string[] = [];
  now.forEach((text, key) => {
    if (!was.has(key)) added.push(text);
  });
  const gone: string[] = [];
  was.forEach((text, key) => {
    if (!now.has(key)) gone.push(text);
  });
  return { added, gone };
}

/** The versions worth showing in the trail: the ones where the answer actually
 *  MOVED. `FrameLine`'s filter — a pass that checked and found it still standing
 *  is real work, and it is also not a version of the answer. */
export function trail(history: BoardroomAnswer[] | null | undefined): BoardroomAnswer[] {
  return (history ?? []).filter((a) => !a.unchanged && !a.dismissed_at);
}

/** Claims split the way they are read: what it rests on, then what argues
 *  against it — kept separate because an answer that lists its own counter-case
 *  is doing the thing that earns `confident`. */
export function sides(a: Pick<BoardroomAnswer, 'claims'> | null | undefined) {
  const claims = a?.claims ?? [];
  return {
    for: claims.filter((c) => c.side !== 'against'),
    against: claims.filter((c) => c.side === 'against'),
  };
}


// ------------------------------ the slideout ------------------------------
//
// The line at the front of the room says WHERE the answer stands. How it got
// there — the reasons, the counter-case, what it rubbed out, every version —
// used to open underneath it and push the tables it rests on off the bottom of
// the screen. It lives in its own slideout now, and what follows is the reading
// of the history that slideout draws.

/** What the counted board says, all of it. The line keeps the short form
 *  (`supportWords`); the slideout has room for the counts that line drops —
 *  what argues against it, and the pains and ideas it stands on. */
export function boardWords(a: Pick<BoardroomAnswer, 'support'> | null | undefined): string {
  const s = a?.support ?? {};
  const bits = [supportWords(a)];
  if (s.against) bits.push(`${s.against} against`);
  if (s.pains) bits.push(`${s.pains} pain${s.pains === 1 ? '' : 's'}`);
  if (s.ideas) bits.push(`${s.ideas} idea${s.ideas === 1 ? '' : 's'}`);
  return bits.filter(Boolean).join(' · ');
}

/** What is being chosen and which way — the pair that makes a reversal
 *  checkable rather than a matter of opinion. Empty when there is no subject. */
export function subjectWords(a: Pick<BoardroomAnswer, 'subject' | 'direction'> | null | undefined): string {
  const subject = (a?.subject ?? '').trim();
  if (!subject) return '';
  const way = a?.direction === 'for' ? 'for' : a?.direction === 'against' ? 'against' : 'still open';
  return `${subject} · ${way}`;
}

/** Why the answerer is not answering, in the room's words, or null when there
 *  is nothing wrong. A parked answerer used to look exactly like one that had
 *  not started: the reason was on the run and nothing read it.
 *
 *  "the answer still stands" is a pass that WORKED, so it is not a note — the
 *  timeline shows those checks. "nothing to answer from" is the room being
 *  early, which is quiet rather than bad. */
export function answererNote(
  answering: Pick<BoardroomAnswering, 'note' | 'working'> | null | undefined,
): { text: string; tone: 'bad' | 'quiet' } | null {
  const note = (answering?.note ?? '').trim();
  if (!note || answering?.working) return null;
  if (note === 'the answer still stands') return null;
  if (note === 'nothing to answer from') return { text: 'waiting for findings to answer from', tone: 'quiet' };
  if (note === 'out of credits') return { text: 'not answering: out of credits', tone: 'bad' };
  return { text: `not answering: ${note}`, tone: 'bad' };
}

/** One version on the timeline, read against the one it replaced. */
export interface VersionEntry {
  kind: 'version';
  answer: BoardroomAnswer;
  /** The answer standing when this one was written (`previous_id`), if it is
   *  in the history we were given. */
  before: BoardroomAnswer | null;
  /** The rung it moved FROM, only when the rung moved. */
  strengthFrom: string | null;
  /** Reasons it started and stopped giving, by claim key. */
  diff: ClaimDiff;
  /** The question it was answering, only when that differs from the one its
   *  predecessor answered — the room's DIRECTION changing, which a version
   *  otherwise looks identical to. */
  questionNow: string | null;
  current: boolean;
  dismissed: boolean;
  /** A person brought an earlier answer back. */
  restored: boolean;
}

/** A run of passes that read the board and found the answer still stood. Real
 *  work, and not a version: grouped, so the trail shows the room was checking
 *  without a row per check. */
export interface CheckedEntry {
  kind: 'checked';
  count: number;
  /** Lookups done by the most recent of them — how much board it checked against. */
  lookups: number;
}

export type TimelineEntry = VersionEntry | CheckedEntry;

const EMPTY_DIFF: ClaimDiff = { added: [], gone: [] };

/**
 * The history as a story, newest first.
 *
 * Every version is read against `previous_id` — the answer that was standing
 * when it was written — rather than the row next to it, because a dismissal or
 * a restore puts rows side by side that were never each other's predecessor. A
 * keep row copies what it kept, so reading against one is reading against the
 * answer it kept.
 *
 * When the predecessor fell off the end of the history (the API sends the last
 * forty), there is nothing to compare against, so there is no diff and no
 * question change rather than a version that appears to have added every claim
 * it has.
 */
export function answerTimeline(
  history: BoardroomAnswer[] | null | undefined,
  currentId: number | null | undefined,
): { entries: TimelineEntry[]; truncated: boolean } {
  const rows = history ?? [];
  const byId = new Map(rows.map(a => [a.id, a]));
  const entries: TimelineEntry[] = [];
  let truncated = false;
  // THE VERSION STANDING NOW. The current answer is the latest row nobody
  // dismissed — which is often a KEEP row, a pass that re-read the board and
  // changed nothing. Keep rows are not versions, so follow it back to the
  // version it kept, or no row on the timeline would say "standing now" and the
  // one that is would offer to be brought back onto itself.
  let standing = currentId != null ? byId.get(currentId) : undefined;
  for (let hops = 0; standing?.unchanged && standing.previous_id != null && hops < rows.length; hops++) {
    standing = byId.get(standing.previous_id);
  }
  const standingId = standing && !standing.unchanged ? standing.id : currentId ?? null;

  for (const a of rows) {
    if (a.unchanged) {
      const last = entries[entries.length - 1];
      if (last && last.kind === 'checked') last.count += 1;
      else entries.push({ kind: 'checked', count: 1, lookups: a.based_on_lookups_done ?? 0 });
      continue;
    }
    const before = a.previous_id != null ? byId.get(a.previous_id) ?? null : null;
    if (a.previous_id != null && !before) truncated = true;
    const strengthFrom =
      before && (before.strength ?? '') !== (a.strength ?? '') ? before.strength : null;
    const q = (a.question ?? '').trim();
    const qBefore = (before?.question ?? '').trim();
    entries.push({
      kind: 'version',
      answer: a,
      before,
      strengthFrom,
      diff: before ? claimDiff(before, a) : EMPTY_DIFF,
      questionNow: before && q && qBefore && q !== qBefore ? q : null,
      current: a.id === standingId,
      dismissed: Boolean(a.dismissed_at),
      restored: a.copied_from_id != null,
    });
  }
  return { entries, truncated };
}

/** The slideout's door, in words: "why · 6 versions". */
export function answerDoorLabel(history: BoardroomAnswer[] | null | undefined): string {
  const n = trail(history).length;
  return n > 1 ? `why · ${n} versions` : 'why';
}
